import { logger } from "@/utils/logger";

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useFormContext } from '@/contexts/FormContext';
import type { DepartmentHours } from '@/utils/messageParser/types';
import SecureStorage from '@/utils/secureStorage';
import { useLanguage } from '@/contexts/LanguageContext';
import { DealerManager } from '@/utils/dealerManager';
import { storeServiceFormUrls, getServiceFormUrl } from '@/utils/cookieUtils';
import { getSessionStartTime } from '@/utils/sessionBoundary';

interface ServiceFormData {
  form: string;
  FormTitle?: string;
  vehicleVin?: string;
  openingHours?: DepartmentHours[];
  messageTimestamp?: Date | number; // Optional timestamp of when this message was created
}

interface ServiceFormMessageProps {
  data: ServiceFormData;
  messageTimestamp?: Date | number; // Pass message timestamp to prevent activation of restored messages
}

const ServiceFormMessage: React.FC<ServiceFormMessageProps> = ({ data, messageTimestamp }) => {
  const { setActiveForm, setOpeningHours, updateFormData, activeForm } = useFormContext();
  const { language, t } = useLanguage();
  const [serviceFormMode, setServiceFormMode] = useState<'Site' | 'Chat'>('Chat');
  const [dealerReady, setDealerReady] = useState(false);
  const formActivatedRef = useRef(false);
  const sessionBoundaryCheckedRef = useRef(false);

  // Check dealer's service form mode and store URLs - re-checks until dealer is ready
  useEffect(() => {
    const checkDealerConfig = () => {
      const dealer = DealerManager.getCurrentDealer();
      
      if (!dealer) {
        logger.log('⏳ ServiceFormMessage: Dealer config not ready yet, will retry...');
        // Retry after a short delay
        setTimeout(checkDealerConfig, 200);
        return;
      }
      
      // Dealer is ready
      logger.log('🔍 ServiceFormMessage: Current dealer:', dealer.dealer_id, 'service_form_mode:', dealer.service_form_mode);
      
      if (dealer.service_form_mode === 'Site') {
        logger.log('✅ ServiceFormMessage: Setting serviceFormMode to Site');
        setServiceFormMode('Site');
        storeServiceFormUrls();
      } else {
        logger.log('ℹ️ ServiceFormMessage: Using Chat mode');
        setServiceFormMode('Chat');
      }
      
      setDealerReady(true);
    };
    
    checkDealerConfig();
  }, []);

  // Memoize opening hours setter to prevent unnecessary re-renders
  const setOpeningHoursOptimized = useCallback(() => {
    logger.log('🕐 ServiceFormMessage: Setting opening hours:', data.openingHours);
    if (data.openingHours) {
      setOpeningHours(data.openingHours);
      logger.log('🕐 ServiceFormMessage: Opening hours set successfully');
    } else {
      logger.log('🕐 ServiceFormMessage: No opening hours data to set');
    }
  }, [data.openingHours, setOpeningHours]);

  // Store opening hours in context when component mounts
  useEffect(() => {
    setOpeningHoursOptimized();
  }, [setOpeningHoursOptimized]);

  // Auto-activate the form when component mounts - MUST be before any conditional returns
  // Close the form if we're in Site mode (external form) AND we have a valid URL
  useEffect(() => {
    if (data.form === 'Service' && serviceFormMode === 'Site' && dealerReady) {
      // Check if we actually have a URL configured before closing the embedded form
      const dealer = DealerManager.getCurrentDealer();
      const hasUrl = (language === 'fr' ? dealer?.service_form_url_fr : dealer?.service_form_url_en) || getServiceFormUrl(language);
      
      if (hasUrl) {
        logger.log('🚫 ServiceFormMessage: Closing embedded form - using external Site form mode');
        if (activeForm === 'Service') {
          setActiveForm(null);
        }
      } else {
        logger.log('⚠️ ServiceFormMessage: Site mode but no URL configured, will use embedded form');
      }
    }
  }, [serviceFormMode, dealerReady, activeForm, setActiveForm, data.form, language]);

  useEffect(() => {
    const activateForm = async () => {
      try {
        // CRITICAL: Don't activate embedded form if we're showing external Site form link
        // BUT only if we actually have a URL configured
        if (data.form === 'Service' && serviceFormMode === 'Site') {
          const dealer = DealerManager.getCurrentDealer();
          const hasUrl = (language === 'fr' ? dealer?.service_form_url_fr : dealer?.service_form_url_en) || getServiceFormUrl(language);
          
          if (hasUrl) {
            logger.log('🚫 ServiceFormMessage: Skipping embedded form activation - using external Site form');
            return;
          } else {
            logger.log('⚠️ ServiceFormMessage: Site mode but no URL, proceeding with embedded form activation');
          }
        }
        
        // CRITICAL: Check if this message is from a previous session
        // Only activate forms for messages from the current session
        if (!sessionBoundaryCheckedRef.current) {
          const sessionStart = getSessionStartTime();
          const msgTime = messageTimestamp 
            ? (messageTimestamp instanceof Date ? messageTimestamp.getTime() : messageTimestamp)
            : Date.now(); // Assume current session if no timestamp provided
          
          if (msgTime < sessionStart) {
            logger.log('🚫 ServiceFormMessage: Skipping form activation - message is from previous session');
            logger.log('   Session started:', new Date(sessionStart).toISOString());
            logger.log('   Message timestamp:', new Date(msgTime).toISOString());
            sessionBoundaryCheckedRef.current = true;
            return;
          }
          sessionBoundaryCheckedRef.current = true;
        }
        
        // Use ref to prevent multiple activations from the same component instance
        if (formActivatedRef.current) {
          logger.log('🔄 ServiceFormMessage: Form already activated by this instance');
          return;
        }

        logger.log('🔄 ServiceFormMessage: useEffect triggered for form:', data.form);
        
        // Validate data
        if (!data || !data.form || data.form === 'None') {
          logger.log('🔄 ServiceFormMessage: No valid form type');
          return;
        }
        
        // Check if this form type has already been submitted in this session
        let submittedForms = [];
        try {
          submittedForms = (await SecureStorage.getItem('submittedFormsInSession')) || [];
        } catch (error) {
          logger.warn('Failed to read submittedFormsInSession from SecureStorage, resetting to empty array:', error);
          await SecureStorage.setItem('submittedFormsInSession', []);
        }
        if (submittedForms.includes(data.form)) {
          logger.log('🔄 ServiceFormMessage: Form already submitted in session');
          return;
        }
        
        // Check if form was recently closed by user to prevent reactivation
        const lastFormCloseTime = localStorage.getItem('lastFormCloseTime');
        const now = Date.now();
        if (lastFormCloseTime && (now - parseInt(lastFormCloseTime)) < 10000) { // 10 second grace period
          logger.log('🔄 ServiceFormMessage: Form recently closed');
          return;
        }
        
        // Prevent setting the same form repeatedly
        if (activeForm === data.form) {
          logger.log('🔄 ServiceFormMessage: Form already active, skipping setActiveForm call');
          return;
        }
        
        logger.log('🔄 ServiceFormMessage: Activating form:', data.form, 'Current active form:', activeForm);
        
        // Mark as activated
        formActivatedRef.current = true;
        
        // Set the form type with a small delay to prevent race conditions
        setTimeout(() => {
          try {
            if (data.form === 'Service') {
              logger.log('✅ ServiceFormMessage: Setting activeForm to Service');
              setActiveForm('Service');
              // Collapse chat after activating embedded service form
              window.dispatchEvent(new CustomEvent('d2c-collapse-chat'));
            } else if (data.form === 'Appointment') {
              logger.log('✅ ServiceFormMessage: Setting activeForm to Appointment');
              setActiveForm('Appointment');
              // Collapse chat after activating embedded appointment form
              window.dispatchEvent(new CustomEvent('d2c-collapse-chat'));
            }
            
            // Pass the FormTitle to the form data
            if (data.FormTitle) {
              updateFormData({ FormTitle: data.FormTitle });
            }
          } catch (innerError) {
            console.error('❌ ServiceFormMessage: Error in setTimeout:', innerError);
          }
        }, 50);
      } catch (error) {
        console.error('❌ ServiceFormMessage: Error activating form:', error);
      }
    };

    activateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.form, data.FormTitle, serviceFormMode, dealerReady, language]);
  
  // NOW all hooks are done - safe to do conditional rendering below
  
  // Determine what to render - calculate this AFTER all hooks
  // Only render external form if we're in Site mode AND have a valid URL
  let externalServiceFormUrl = '';
  
  if (data.form === 'Service' && serviceFormMode === 'Site') {
    logger.log('🔍 ServiceFormMessage: Checking Site mode - language:', language);
    
    // First try to get URL from dealer configuration
    const dealer = DealerManager.getCurrentDealer();
    const dealerUrl = language === 'fr' ? dealer?.service_form_url_fr : dealer?.service_form_url_en;
    logger.log('🔍 ServiceFormMessage: Dealer config URL:', dealerUrl);
    
    // Fallback to cookie-based URL (from localStorage) if not in dealer config
    const cookieUrl = getServiceFormUrl(language);
    logger.log('🔍 ServiceFormMessage: Cookie/localStorage URL:', cookieUrl);
    
    externalServiceFormUrl = dealerUrl || cookieUrl || '';
    
    logger.log('🔍 ServiceFormMessage: Final serviceFormUrl:', externalServiceFormUrl);
  }
  
  const shouldRenderExternalForm = data.form === 'Service' && serviceFormMode === 'Site' && !!externalServiceFormUrl;
  
  // If we have an external URL, render it
  if (shouldRenderExternalForm && externalServiceFormUrl) {
    logger.log('✅ ServiceFormMessage: Rendering external form link');
    return (
      <div className="mb-2 w-full flex flex-col items-center justify-center space-y-2 p-4 bg-background border border-border rounded-lg">
        <p className="text-foreground text-sm text-center">
          {t('service.form.external.message')}
        </p>
        <Button 
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => {
            window.open(externalServiceFormUrl, '_blank');
            // Collapse chat after opening external service form
            window.dispatchEvent(new CustomEvent('d2c-collapse-chat'));
          }}
        >
          {t('service.form.external.button')}
        </Button>
      </div>
    );
  }
  
  // Fall back to normal service form activation if needed
  if (shouldRenderExternalForm) {
    logger.log('⚠️ ServiceFormMessage: ServiceForm URL is empty, falling back to embedded form');
  }
  
  logger.log('🔍 ServiceFormMessage: data.form:', data.form, 'serviceFormMode:', serviceFormMode, 'activeForm:', activeForm);

  // Handle Carfax form specifically
  if (data.form === 'Carfax') {
    return (
      <div className="mb-2 flex flex-col items-center space-y-2">
        <p className="text-gray-800 text-sm text-center">
          {data.FormTitle || 'Get your free Carfax report by SMS!'}
        </p>
        <p className="text-gray-600 text-xs text-center">
          VIN: {data.vehicleVin || 'Not available'}
        </p>
        <Button 
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => {
            // This should trigger the Carfax form
          }}
        >
          Request Carfax Report
        </Button>
      </div>
    );
  }

  // Don't render anything - just activate the form
  return null;
};

export default ServiceFormMessage;
